import { FC } from 'react';
import { useQuery } from '@apollo/client';
import { Box, HStack, Skeleton, SkeletonProps, VStack } from '@chakra-ui/react';
import { printFormat as formatIBAN } from 'iban';

import { BankAccountStatus } from '__generated__/GQL';
import { BankAccountClosedError } from 'common/errors';
import getRibUrl from 'common/getRibUrl';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import { Copyable } from 'components/_core/Copyable';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';

import { IbanPreviewDocument } from './ibanPreview.gql';
import locales from './locales';

const DEFAULT_VALUES = {
  bic: '',
  iban: '',
  ownerName: '',
};

interface IbanPreviewProps {
  bankAccountId?: string;
  loading: boolean;
}

const SkeletonText: FC<SkeletonProps> = ({
  height = '8px',
  marginY = '6px',
  width,
  ...props
}) => {
  return (
    <Box h="25px">
      <Skeleton
        borderRadius="radius-24"
        endColor="grey.300"
        startColor="grey.100"
        /**
         * Remove dimension and spacing props when not visible.
         * Otherwise, the skeleton breaks the layout of the component it's wrapping.
         */
        {...(props.isLoaded ? {} : { height, marginY, width })}
        {...props}
      />
    </Box>
  );
};

const IbanPreview: FC<IbanPreviewProps> = ({
  bankAccountId,
  loading: loadingProp,
}) => {
  const currentLanguage = useCurrentLanguage();
  const {
    data,
    error,
    loading: loadingData,
  } = useQuery(IbanPreviewDocument, {
    fetchPolicy: 'network-only',
    skip: !bankAccountId,
    variables: {
      bankAccountId: bankAccountId as string,
    },
  });

  if (error) {
    return <ErrorCard />;
  }

  if (data?.viewer.bankAccount.status === BankAccountStatus.Closed) {
    throw new BankAccountClosedError();
  }

  const loading = loadingProp || loadingData;
  const { bic, iban, ownerName } = data?.viewer.bankAccount ?? DEFAULT_VALUES;
  const formattedIBAN = formatIBAN(iban);
  const allAsText = [
    [locales.accountHolder, ownerName],
    [locales.iban, formattedIBAN],
    [locales.bic, bic],
    [locales.bicIntermediary, locales.swiftBIC],
  ]
    .map((line) => line.join('\n'))
    .join('\n\n');

  return (
    <SunshineCard.Group borderRadius="radius-12">
      <SunshineCard borderRadius="radius-12" padding="space-16">
        <VStack alignItems="flex-start" spacing="space-4">
          <Text color="grey.600">{locales.accountHolder}</Text>
          <SkeletonText isLoaded={!loading} width="300px">
            <Copyable text={ownerName} withHint>
              <HStack alignItems="center" spacing="space-4">
                <Text fontWeight="weight-500" variant="primary">
                  {ownerName}
                </Text>
                <SunshineIcon color="grey.600" name="copy" size="icon-20" />
              </HStack>
            </Copyable>
          </SkeletonText>
        </VStack>
      </SunshineCard>
      <SunshineCard borderRadius="radius-12" padding="space-16">
        <VStack alignItems="stretch" spacing="space-16">
          <HStack justifyContent="space-between" spacing="space-32">
            <VStack alignItems="flex-start" spacing="space-4">
              <Text color="grey.600">{locales.iban}</Text>
              <SkeletonText isLoaded={!loading} width="300px">
                <Copyable text={formattedIBAN} withHint>
                  <HStack alignItems="center" spacing="space-4">
                    <Text fontWeight="weight-500" variant="primary">
                      {formattedIBAN}
                    </Text>
                    <SunshineIcon color="grey.600" name="copy" size="icon-20" />
                  </HStack>
                </Copyable>
              </SkeletonText>
            </VStack>
            <VStack alignItems="flex-start" spacing="space-4">
              <Text color="grey.600">{locales.bic}</Text>
              <SkeletonText isLoaded={!loading} width="137px">
                <Copyable text={bic} withHint>
                  <HStack alignItems="center" spacing="space-4">
                    <Text fontWeight="weight-500" variant="primary">
                      {bic}
                    </Text>
                    <SunshineIcon color="grey.600" name="copy" size="icon-20" />
                  </HStack>
                </Copyable>
              </SkeletonText>
            </VStack>
          </HStack>
          <Callout hideIcon>
            <Callout.Title>{locales.internationalTransfer}</Callout.Title>
            <Callout.Content>
              {locales.swiftDetails}
              <Copyable text={locales.swiftBIC} withHint>
                <Box display="inline">
                  <HStack alignItems="center" spacing="space-4">
                    <Text fontWeight="weight-500" size="sm" variant="secondary">
                      {locales.swiftBIC}
                    </Text>
                    <SunshineIcon color="grey.600" name="copy" size="icon-16" />
                  </HStack>
                </Box>
              </Copyable>
            </Callout.Content>
          </Callout>
        </VStack>
      </SunshineCard>
      <SunshineCard borderRadius="radius-12" padding="space-16">
        <HStack justifyContent="space-between">
          <Copyable text={allAsText}>
            <Button icon="copy" variant="secondary">
              {locales.copyAll}
            </Button>
          </Copyable>
          <Button
            {...(bankAccountId
              ? {
                  as: 'a',
                  href: getRibUrl(bankAccountId, currentLanguage),
                  target: '_blank',
                }
              : {})}
            icon="download"
          >
            {locales.rib}
          </Button>
        </HStack>
      </SunshineCard>
    </SunshineCard.Group>
  );
};

export default IbanPreview;
